import { useMemo, useCallback, useEffect } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Form, Row, Col, Label, Button, FormGroup, Input } from "reactstrap";

const PropertySelector = ({
    propertyList,
    selectedProperty,
    onPropertySelectionChange,
    onAddClick,
    isLoading,
}) => {
    const currentProperty = useMemo(() =>
        propertyList.find(prop => prop._id === selectedProperty.property_id),
        [propertyList, selectedProperty.property_id]
    );

    const buildingList = useMemo(() => {
        if (!currentProperty?.building) return [];
        return currentProperty.building
            .filter(b => b && b.buildingName)
            .map(b => b.buildingName);
    }, [currentProperty]);

    const isAddButtonDisabled = useMemo(() => {
        const hasPropertyId = Boolean(selectedProperty.property_id);
        const hasBuildingName = Boolean(selectedProperty.building_name);
        const hasUnitNo = Boolean(selectedProperty.unit_no);

        if (buildingList.length > 0 && !hasBuildingName) {
            return true;
        }

        return !hasPropertyId || !hasUnitNo;
    }, [selectedProperty.property_id, selectedProperty.building_name, selectedProperty.unit_no, buildingList.length]);

    const propertyOptions = useMemo(() =>
        propertyList.map(prop => ({
            value: prop._id,
            label: `${prop.title}, ${prop.location}`
        })),
        [propertyList]
    );

    const buildingOptions = useMemo(() =>
        buildingList.map(building => ({
            value: building,
            label: building
        })),
        [buildingList]
    );

    const selectedPropertyOption = useMemo(() =>
        propertyOptions.find(option => option.value === selectedProperty.property_id) || null,
        [propertyOptions, selectedProperty.property_id]
    );

    const selectedBuildingOption = useMemo(() =>
        buildingOptions.find(option => option.value === selectedProperty.building_name) || null,
        [buildingOptions, selectedProperty.building_name]
    );

    useEffect(() => {
        if (buildingList.length === 1 && !selectedProperty.building_name && selectedProperty.property_id) {
            onPropertySelectionChange({
                ...selectedProperty,
                building_name: buildingList[0]
            });
        }
    }, [buildingList, selectedProperty, onPropertySelectionChange]);

    useEffect(() => {
        if (currentProperty && selectedProperty.property_id) {
            const updatedProperty = {
                ...selectedProperty,
                address: currentProperty.address || selectedProperty.address || '',
                postal_code: currentProperty.postal_code || selectedProperty.postal_code || '',
            };

            if (updatedProperty.address !== selectedProperty.address ||
                updatedProperty.postal_code !== selectedProperty.postal_code) {
                onPropertySelectionChange(updatedProperty);
            }
        }
    }, [currentProperty, selectedProperty, onPropertySelectionChange]);

    const handlePropertyChange = useCallback((selectedOption) => {
        const newPropertyId = selectedOption ? selectedOption.value : '';
        const selectedProp = propertyList.find(prop => prop._id === newPropertyId);

        onPropertySelectionChange({
            ...selectedProperty,
            property_id: newPropertyId,
            building_name: '',
            unit_no: '',
            address: selectedProp?.address || '',
            postal_code: selectedProp?.postal_code || ''
        });
    }, [onPropertySelectionChange, propertyList, selectedProperty]);

    const handleBuildingChange = useCallback((selectedOption) => {
        const newBuildingName = selectedOption ? selectedOption.value : '';

        onPropertySelectionChange({
            ...selectedProperty,
            building_name: newBuildingName
        });
    }, [selectedProperty, onPropertySelectionChange]);

    const handleUnitNoChange = useCallback((e) => {
        const unitNo = e.target.value;

        onPropertySelectionChange({
            ...selectedProperty,
            unit_no: unitNo
        });
    }, [selectedProperty, onPropertySelectionChange]);

    const handleStatusChange = useCallback((e) => {
        const newStatus = e.target.value;

        onPropertySelectionChange({
            ...selectedProperty,
            status: newStatus
        });
    }, [selectedProperty, onPropertySelectionChange]);

    const customSelectStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999
        }),
        control: (base, state) => ({
            ...base,
            zIndex: 2,
            backgroundColor: buildingList.length === 1 ? '#f8f9fa' : base.backgroundColor,
            cursor: buildingList.length === 1 ? 'not-allowed' : 'default'
        })
    };

    return (
        <Form className='mb-3'>
            <Row className="mb-3">
                <Col md={8}>
                    <FormGroup check inline>
                        <Input
                            type="radio"
                            name="status"
                            value="all"
                            checked={selectedProperty.status === 'all'}
                            onChange={handleStatusChange}
                            id="allFilter"
                        />
                        <Label check for="allFilter">
                            All
                        </Label>
                    </FormGroup>
                    <FormGroup check inline>
                        <Input
                            type="radio"
                            name="status"
                            value="pending"
                            checked={selectedProperty.status === 'pending'}
                            onChange={handleStatusChange}
                            id="pendingFilter"
                        />
                        <Label check for="pendingFilter">
                            Pending
                        </Label>
                    </FormGroup>
                    <FormGroup check inline>
                        <Input
                            type="radio"
                            name="status"
                            value="awaiting_auditor"
                            checked={selectedProperty.status === 'awaiting_auditor'}
                            onChange={handleStatusChange}
                            id="awaitingAuditorFilter"
                        />
                        <Label check for="awaitingAuditorFilter">
                            Awaiting Auditor
                        </Label>
                    </FormGroup>
                    <FormGroup check inline>
                        <Input
                            type="radio"
                            name="status"
                            value="approved"
                            checked={selectedProperty.status === 'approved'}
                            onChange={handleStatusChange}
                            id="approvedFilter"
                        />
                        <Label check for="approvedFilter">
                            Approved
                        </Label>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>Property</Label>
                        <Select
                            value={selectedPropertyOption}
                            onChange={handlePropertyChange}
                            options={propertyOptions}
                            placeholder="Select Property"
                            isClearable
                            name="property_id"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isDisabled={isLoading}
                        />
                    </FormGroup>
                </Col>

                {buildingList.length > 0 && (
                    <Col md={3}>
                        <FormGroup>
                            <Label>Building Name</Label>
                            <Select
                                value={selectedBuildingOption}
                                onChange={handleBuildingChange}
                                options={buildingOptions}
                                placeholder="Select Building"
                                isClearable={buildingList.length > 1}
                                isDisabled={buildingList.length === 1 || isLoading}
                                name="building_name"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </FormGroup>
                    </Col>
                )}

                <Col md={3}>
                    <FormGroup>
                        <Label>Unit No</Label>
                        <Input
                            type="text"
                            name="unit_no"
                            value={selectedProperty.unit_no || ''}
                            onChange={handleUnitNoChange}
                            placeholder="Enter Unit Number"
                            disabled={isLoading}
                        />
                    </FormGroup>
                </Col>

                <Col md={2} className="d-flex align-items-end">
                    <Button
                        color="primary"
                        className="ms-2"
                        onClick={onAddClick}
                        disabled={isAddButtonDisabled || isLoading}
                    >
                        <FeatherIcon icon="plus-circle" size={18} style={{ marginRight: '8px' }} />
                        Generate SOA
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export { PropertySelector };